import { i18n } from '../behaviors/languages';
import { errors } from '../tools/errors';
import { client } from './worktango/apollo';
import * as log from './log';
import { ApolloError, DocumentNode } from '@apollo/client';
import { GraphQLError } from 'graphql';

/**
 * Find the first instance of a 'OperationDefinition' and return the operation
 * name (only supports 1 'OperationDefinition' per query). Other definitions
 * may be of the kind 'FragmentDefinition' if it imports other fragments.
 *
 * The result of loading the query comes from `graphql-tag` under the hood.
 *
 * @link https://github.com/apollographql/graphql-tag
 * @link https://github.com/evenchange4/graphql.macro
 *
 * @example query shape (comes from `graphql.macro`'s `loader()` function):
 *
 * {
 *   definitions: [{
 *     operation: 'mutation',
 *     directives: [...],
 *     kind: 'OperationDefinition',
 *     name: {
 *       kind: 'Name',
 *       value: 'myQueryName'
 *     },
 *     selectionSet: {...},
 *     variableDefinitions: [...],
 *     length: 1
 *   }],
 *   kind: 'Document',
 *   loc: {
 *     start: 0,
 *     end: 794,
 *     source: {}
 *   }
 * }
 */
function getOperationFromAbstractSyntaxTree(definitions: DocumentNode['definitions']) {
  if (!Array.isArray(definitions)) {
    return errors.createError({
      message: 'GraphQL definitions must be an Array',
    });
  }

  const opDef = definitions.find((def) => def.kind === 'OperationDefinition');

  return opDef.operation;
}

type WithErrorParams =
  | { graphQLErrors?: GraphQLError[] }
  | { networkError?: { result: { errors: GraphQLError[] } } }
  | { errors?: Error[] }
  | { message?: string }
  | string;

/**
 * Takes an error response object created by Apollo client and extracts the
 * plain error message and returns an Error object that includes that message.
 * Fall back to `message` attribute, or just the argument itself, if no matches
 * exist. Different parts of the API server respond with different kinds of
 * errors which is difficult to handle, so this function tries to normalize that
 * into a simple error message string for the rest of the app to respond to.
 *
 * @example error response object shape from Apollo client:
 *
 * {
 *   graphQLErrors: [{
 *     message: 'My error message',
 *   }],
 *   message: 'GraphQL Error: My error message'
 * }
 */
function withError(err: { graphQLErrors: GraphQLError[] }): { message: string; type?: string | unknown };
function withError(err: { networkError: { result: { errors: GraphQLError[] } } }): Error;
function withError(err: { errors: Error[] }): string;
function withError(err: { message: string } | string): Error;
function withError(err: WithErrorParams): Error | { message: string; type?: string | unknown } | string {
  if (!err) {
    return new Error('No error provided');
  }

  if (typeof err === 'string') {
    return new Error(err);
  }

  if (typeof err !== 'object') {
    return new Error('Invalid error object');
  }

  if ('graphQLErrors' in err && err.graphQLErrors?.[0] !== undefined) {
    const rawError = err.graphQLErrors[0];
    return {
      message: rawError.message,
      type: rawError.extensions.type || undefined,
    };
  }

  if ('networkError' in err && !!err.networkError?.result?.errors?.length) {
    const firstError = err.networkError?.result?.errors[0];
    const translationKey = firstError?.extensions?.translationKey;
    const translatedError = translationKey ? i18n.t(translationKey) : '';
    const errMessage = translatedError || firstError?.message || err;
    return new Error(errMessage);
  }

  if ('errors' in err && Array.isArray(err?.errors) && err?.errors?.[0] !== undefined) return err.errors[0].message;

  return new Error('message' in err && err.message !== '' ? err.message : 'Unknown error');
}

/**
 * Make GraphQL network request using Apollo module.
 *
 * @deprecated Use the typed Apollo hooks generated by codegen (`useXxxQuery`, `useXxxMutation`)
 * from `~/gql/graphql` instead. Do not use this function for new work.
 */
async function graphqlRequest<TData extends { data?: unknown }>(
  queryDocument: DocumentNode,
  variables: Record<string, unknown> = {},
): Promise<TData> {
  return new Promise<TData>((resolve, reject) => {
    if (!queryDocument.definitions) {
      return reject(new Error('graphqlQuery has no .definitions'));
    }

    const operation = getOperationFromAbstractSyntaxTree(queryDocument.definitions);
    const apolloRequest = operation === 'mutation' ? client.mutate : client.query;

    return apolloRequest({
      fetchPolicy: 'no-cache',
      [operation]: queryDocument,
      variables,
    })
      .then((res) => {
        resolve(res);
      })
      .catch((err) => {
        log.debug(`GraphQL Request Error: ${err}`);
        reject(withError(err));
      });
  });
}

function hasGraphQLErrors(result: unknown): result is { errors: GraphQLError[] } {
  return typeof result === 'object' && result !== null && 'errors' in result;
}

function hasGraphQLNetworkError(result: unknown): result is { error: ApolloError } {
  return typeof result === 'object' && result !== null && 'error' in result;
}

export { graphqlRequest, hasGraphQLErrors, hasGraphQLNetworkError, getOperationFromAbstractSyntaxTree, withError };
